import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

import { cn } from "../../../lib/utils";
import { SkeletonCard } from "./SkeletonCard";
import { SkeletonMetric } from "./SkeletonMetric";
import { SkeletonText } from "./SkeletonText";

function SkeletonStage({
  children,
  className,
  label,
  testId,
}: {
  children: ReactNode;
  className?: string;
  label: string;
  testId: string;
}) {
  const prefersReducedMotion = useReducedMotion() === true;
  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      aria-label={label}
      className={cn("space-y-5", className)}
      data-testid={testId}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
      role="status"
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { duration: 0.24, ease: [0.2, 0, 0, 1] }
      }
    >
      <span className="sr-only">{label}</span>
      {children}
    </motion.div>
  );
}

function HeaderSkeleton({
  compact = false,
  withAction = false,
}: {
  compact?: boolean;
  withAction?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 px-1">
      <div
        className={cn(
          "space-y-3",
          compact ? "w-full max-w-xl" : "w-full max-w-3xl",
        )}
      >
        <span className="block h-3 w-28 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />
        <span className="block h-8 w-64 animate-pulse rounded-full bg-muted motion-reduce:animate-none sm:w-80" />
        <SkeletonText lines={2} width="72%" />
      </div>
      {withAction ? (
        <span className="block h-10 w-36 animate-pulse rounded-pill bg-muted motion-reduce:animate-none" />
      ) : null}
    </div>
  );
}

export function OverviewSkeleton({
  journeyRailDismissed = false,
}: {
  journeyRailDismissed?: boolean;
}) {
  return (
    <SkeletonStage
      label="Loading dashboard overview"
      testId="overview-skeleton"
    >
      <HeaderSkeleton />
      <SkeletonCard className="rounded-3xl p-5 sm:p-6">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="space-y-4">
            <SkeletonText lines={2} width="50%" />
            <span className="block h-24 animate-pulse rounded-2xl bg-muted motion-reduce:animate-none" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <SkeletonMetric label="Readiness score" />
            <SkeletonMetric label="Target" />
          </div>
        </div>
      </SkeletonCard>
      {journeyRailDismissed ? null : (
        <SkeletonCard className="rounded-3xl p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
            <SkeletonText lines={5} width="62%" />
            <SkeletonText lines={4} width="78%" />
          </div>
        </SkeletonCard>
      )}
      <div className="grid gap-3 xl:grid-cols-2">
        <SkeletonCard className="min-h-48 rounded-3xl" />
        <SkeletonCard className="min-h-48 rounded-3xl" />
      </div>
      <SkeletonCard className="min-h-36 rounded-3xl" />
    </SkeletonStage>
  );
}

export function SettingsPageSkeleton() {
  return (
    <SkeletonStage label="Loading settings" testId="settings-page-skeleton">
      <SkeletonCard className="rounded-3xl p-5">
        <SkeletonText lines={3} width="55%" />
      </SkeletonCard>
      <HeaderSkeleton />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[10.5rem_minmax(0,1fr)] lg:items-start">
        <SkeletonCard className="hidden min-h-44 rounded-3xl lg:block" />
        <div className="min-w-0 space-y-5">
          <SkeletonCard className="min-h-52 rounded-3xl" />
          <SkeletonCard className="min-h-44 rounded-3xl" />
          <SkeletonCard className="min-h-40 rounded-3xl" />
        </div>
      </div>
    </SkeletonStage>
  );
}

export function CalculatorsPageSkeleton() {
  return (
    <SkeletonStage
      label="Loading calculators"
      testId="calculators-page-skeleton"
    >
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="max-w-2xl space-y-3">
          <span className="block h-9 w-56 animate-pulse rounded-full bg-muted motion-reduce:animate-none sm:h-10 sm:w-64" />
          <SkeletonText lines={2} width="68%" />
        </header>

        <div className="space-y-3">
          <SkeletonCard
            className="grid min-h-36 gap-4 rounded-2xl border-primary/15 bg-primary/6 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-start"
            testId="calculators-skeleton-featured-card"
          >
            <div className="max-w-2xl space-y-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="block h-6 w-44 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />
                <span className="block h-6 w-20 animate-pulse rounded-pill bg-primary/10 motion-reduce:animate-none" />
              </div>
              <SkeletonText lines={2} width="76%" />
            </div>
            <span className="block h-10 w-32 animate-pulse rounded-pill bg-primary/25 motion-reduce:animate-none md:self-end" />
          </SkeletonCard>

          <div
            className="grid auto-rows-fr gap-3 md:grid-cols-2"
            data-testid="calculators-skeleton-tool-grid"
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard
                className="flex min-h-36 flex-col justify-between rounded-2xl p-5"
                key={index}
              >
                <div className="space-y-3">
                  <span className="block h-6 w-40 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />
                  <SkeletonText lines={2} width="70%" />
                </div>
                <span className="mt-5 block h-4 w-16 animate-pulse rounded-full bg-primary/20 motion-reduce:animate-none" />
              </SkeletonCard>
            ))}
          </div>
        </div>
      </div>
    </SkeletonStage>
  );
}

export function PropertyPageSkeleton() {
  return (
    <div className="min-h-screen bg-bg-warm px-4 py-5 sm:px-6 lg:px-8">
      <main className="content-shell space-y-5">
        <SkeletonStage label="Loading property" testId="property-page-skeleton">
          <span className="block h-9 w-28 animate-pulse rounded-pill bg-muted motion-reduce:animate-none" />
          <SkeletonCard className="rounded-3xl p-5 sm:p-7">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
              <div className="space-y-5">
                <HeaderSkeleton compact />
                <div className="grid gap-3 sm:grid-cols-3">
                  <SkeletonMetric label="List price" />
                  <SkeletonMetric label="Fit" />
                  <SkeletonMetric label="Status" />
                </div>
                <SkeletonText lines={5} width="72%" />
              </div>
              <SkeletonCard className="min-h-72 rounded-3xl bg-background/70" />
            </div>
          </SkeletonCard>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <SkeletonCard className="min-h-52 rounded-3xl" />
            <SkeletonCard className="min-h-52 rounded-3xl" />
          </div>
          <div className="grid gap-3 lg:grid-cols-3">
            <SkeletonCard className="min-h-48 rounded-3xl" />
            <SkeletonCard className="min-h-48 rounded-3xl" />
            <SkeletonCard className="min-h-48 rounded-3xl" />
          </div>
        </SkeletonStage>
      </main>
    </div>
  );
}

export function AdvisorHydrationSkeleton() {
  return (
    <SkeletonStage
      className="mx-auto w-full max-w-[800px] px-4 py-16"
      label="Loading Ask Sill conversation"
      testId="advisor-hydration-skeleton"
    >
      <div className="space-y-5">
        <div className="mx-auto flex max-w-md flex-col items-center gap-3">
          <span className="block h-10 w-44 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />
          <SkeletonText lines={2} width="62%" />
        </div>
        <SkeletonCard className="mx-auto min-h-[102px] max-w-[800px] rounded-[26px]" />
        <div className="mx-auto grid max-w-[720px] gap-2.5 sm:grid-cols-2">
          <SkeletonCard className="min-h-16 rounded-2xl" />
          <SkeletonCard className="min-h-16 rounded-2xl" />
        </div>
      </div>
    </SkeletonStage>
  );
}

export function PropertiesListSkeleton() {
  return (
    <SkeletonStage label="Loading properties" testId="properties-list-skeleton">
      <SkeletonCard className="rounded-3xl p-4 sm:p-[17px]">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
          <SkeletonText lines={4} width="66%" />
          <div className="grid gap-2">
            <span className="block h-9 animate-pulse rounded-pill bg-muted motion-reduce:animate-none" />
            <span className="block h-9 animate-pulse rounded-pill bg-muted motion-reduce:animate-none" />
          </div>
        </div>
      </SkeletonCard>
      <SkeletonCard className="rounded-3xl p-4 sm:p-[17px]">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
          <SkeletonText lines={4} width="58%" />
          <div className="grid gap-2">
            <span className="block h-9 animate-pulse rounded-pill bg-muted motion-reduce:animate-none" />
            <span className="block h-9 animate-pulse rounded-pill bg-muted motion-reduce:animate-none" />
          </div>
        </div>
      </SkeletonCard>
    </SkeletonStage>
  );
}

export function DocumentsListSkeleton() {
  return (
    <SkeletonStage
      className="p-5 sm:p-6"
      label="Loading documents"
      testId="documents-list-skeleton"
    >
      <div className="grid gap-3">
        <SkeletonCard className="min-h-20 rounded-2xl" />
        <SkeletonCard className="min-h-20 rounded-2xl" />
        <SkeletonCard className="min-h-20 rounded-2xl" />
      </div>
    </SkeletonStage>
  );
}
